import * as React from "react";
import pluginRegistry, {ActionEditProps} from "../pluginRegistry";
import {BaseComponent} from "components/BaseComponent/BaseComponent";
import SpecialVariables from "client/specialVariables";
import {repository} from "clientInstance";
import {ActionSummaryProps} from "../actionSummaryProps";
import {ActionExecutionLocation} from "../../../client/resources/actionExecutionLocation";
import ExpanderSectionHeading from "components/form/Sections/FormSectionHeading";
import FeedResource, {BuiltInFeedId, FeedType} from "client/resources/feedResource";
import InternalLink from "components/Navigation/InternalLink/InternalLink";
import ExpandableFormSection from "components/form/Sections/ExpandableFormSection";
import CommonSummaryHelper from "utils/CommonSummaryHelper/CommonSummaryHelper";
import PackageSelector, {PackageSelectorProperties} from "components/PackageSelector/PackageSelector";
import PackageDownloadOptions, {PackageDownloadOptionsProperties} from "components/PackageDownloadOptions/PackageDownloadOptions";
import WildflyFeatureTemplate, {WildflyStateProperties} from "./wildflyFeatureTemplate";
import routeLinks from "../../../routeLinks";
import {getFeedName} from "../getFeedName";
import { TargetRoles } from "areas/projects/components/DeploymentProcess/ActionDetails";

interface DeployToWildflySummaryState {
    feedName: string;
}

class DeployToWildflyActionSummary extends BaseComponent<ActionSummaryProps, DeployToWildflySummaryState> {
    constructor(props: ActionSummaryProps) {
        super(props);
        this.state = {feedName: null };
    }

    async componentDidMount() {
        this.setState({feedName: await getFeedName(this.getFeedId())});
    }

    render() {
        return <div>
            Deploy a Java application to Wildfly 10+ and Red Hat JBoss EAP 6+
        </div>;
    }

    private getFeedId() {
        return this.props.properties[SpecialVariables.Action.Package.FeedId] as string;
    }
}

interface DeployToWildflyProperties extends PackageSelectorProperties, PackageDownloadOptionsProperties, WildflyStateProperties {
    "Octopus.Action.Package.TransferPath": string;
}

interface DeployToWildflyActionEditState {
    feeds: FeedResource[];
}

export class DeployToWildflyActionEdit extends BaseComponent<ActionEditProps<DeployToWildflyProperties>, DeployToWildflyActionEditState> {
    constructor(props: ActionEditProps<DeployToWildflyProperties>) {
        super(props);

        this.state = {
            feeds: []
        };
    }

    componentDidMount() {
        this.props.doBusyTask(async () => {
            const properties: any = {};
            properties["Octopus.Features.WildflyDeployCLI"] = "True";
            if (!this.props.properties["Octopus.Action.Package.FeedId"]) {
                properties["Octopus.Action.Package.FeedId"] = BuiltInFeedId;
            }
            this.props.setProperties(properties, true);
            this.setState({feeds: await repository.Feeds.all()});
        });
    }

    render() {
        const help = this.state.feeds.length > 0
            ? <span>
                This step is used to deploy a package to one or more machines which may be sourced from an external feed or the Octopus built-in feed.
                You can configure the remote machines to deploy to in the <InternalLink to={routeLinks.infrastructure.root} openInSelf={false}>Infrastructure</InternalLink> tab.
            </span>
            : <span>Choose the package you wish to deploy.</span>;

        return <div>
            <ExpanderSectionHeading title="Package Details"/>
            <ExpandableFormSection
                errorKey="Octopus.Action.Package.PackageId|Octopus.Action.Package.FeedId"
                title="Package"
                summary={CommonSummaryHelper.packageSummary(this.props.properties, this.state.feeds)}
                help={help}>
                <PackageSelector
                    setProperties={this.props.setProperties}
                    properties={this.props.properties}
                    projectId={this.props.projectId}
                    feeds={this.state.feeds}
                    getFieldError={this.props.getFieldError}
                    localNames={this.props.localNames}
                    errors={this.props.errors}
                    feedType={[FeedType.Nuget, FeedType.Maven]}/>
                <PackageDownloadOptions
                    setProperties={this.props.setProperties}
                    properties={this.props.properties}
                    projectId={this.props.projectId}
                    localNames={this.props.localNames}
                    errors={this.props.errors} />
            </ExpandableFormSection>
        </div>;
    }
}

pluginRegistry.registerDeploymentAction({
    executionLocation: ActionExecutionLocation.AlwaysOnTarget,
    actionType: "Octopus.WildFlyDeploy",
    summary: (properties, targetRolesAsCSV) => <DeployToWildflyActionSummary properties={properties} targetRolesAsCSV={targetRolesAsCSV} />,
    edit: DeployToWildflyActionEdit,
    canHaveChildren: (step) => true,
    canBeChild: true,
    targetRoleOption: TargetRoles.Optional,
    hasPackages: (action) => true,
    features: {
        optional: ["Octopus.Features.CustomScripts",
            "Octopus.Features.JsonConfigurationVariables",
            "Octopus.Features.SubstituteInFiles"],
        permanent: ["Octopus.Features.WildflyDeployCLI"]
    }
});


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/Actions/wildfly/deployToWildflyAction.tsx